import { IsofixGlyph } from '../ui/Artwork'
import { CROWN_ARC } from './capShape'
import { AirService, Cabinet, Carriage, CFrame, CropMarks, DragChain, Floor, Table, XAxis } from './kit'
import { CapBody, CapMark, ClicheBase, ClicheTop, CupDrive, CupRod, Fixture, InkCup, PadPartDefs } from './padParts'
import { BULGE_ARCH, BULGE_FLAT, DOME, DOME_CORE, DOME_LIT, DOME_SHADE, DOME_SPEC, LIP, RIM_BACK, RIM_LIT } from './padShape'

/* ==========================================================================
   The pad-printing film: one cycle cliché → silicone pad → ISOFIX cap.
   The pad is the only soft body: it touches at its apex, rolls flat against
   a structural "air" clip (the floor is y 550 at the cliché and the crown arc
   at the cap), bulges at the flanks, and wobbles after the hard stop.
   ========================================================================== */

const { left: ARC_L, right: ARC_R, ctrl: ARC_CTRL } = CROWN_ARC
const AIR = `M-1100 -300H2700V600H${ARC_R[0]}V${ARC_R[1]}Q${ARC_CTRL} ${ARC_L[0]} ${ARC_L[1]}V600H804V550H-1100Z`
const LIT_CRESCENT = 'M-31.4 41.4A52 52 0 1 1 41.4 -31.4A52 52 0 0 0 -31.4 41.4Z'
const CORE_CRESCENT = 'M32.6 -40.6A52 52 0 1 1 -40.6 32.6A52 52 0 0 0 32.6 -40.6Z'
const FACE_AT = (x) => `matrix(1 0 -0.18 0.5 ${x} 550)`
const FACE_UNIT = 'matrix(1 0 -0.18 0.5 0 0)'

function PadDefs({ id }) {
  return (
    <defs>
      <PadPartDefs id={id} />
      <clipPath id={`${id}air`} clipPathUnits="userSpaceOnUse">
        <path d={AIR} />
      </clipPath>
      <clipPath id={`${id}bloom`} clipPathUnits="userSpaceOnUse">
        <circle className="pp-bloom" r="46" />
      </clipPath>
    </defs>
  )
}

/* Guide rods and piston rod: drawn behind the Z cylinder, they slide out of it. */
function PadRods() {
  return (
    <g className="pp-xy">
      <path className="f-cB" d="M-34 140h12v160h-12ZM22 140h12v160h-12ZM-5 140h10v160h-10Z" />
      <path className="f-cA" d="M-34 140h2.4v160h-2.4ZM22 140h2.4v160h-2.4ZM-5 140h2v160h-2Z" />
      <path className="f-cC" d="M-26.2 140h4.2v160h-4.2ZM29.8 140h4.2v160h-4.2ZM1.5 140h3.5v160h-3.5Z" />
    </g>
  )
}

function PadHolder() {
  return (
    <g className="pp-xy">
      <path className="f-aS" d="M84 326L93.4 300V314L84 340Z" />
      <rect className="f-aF" x="-84" y="326" width="168" height="14" />
      <path className="f-aT" d="M-84 326H84L93.4 300H-74.6Z" />
      <path className="l-spec" d="M-84 340V326H84" />
      <path className="f-aS pp-hair" d="M-58 316a5 2.5 0 1 0 10 0a5 2.5 0 1 0-10 0ZM58 316a5 2.5 0 1 0 10 0a5 2.5 0 1 0-10 0Z" />
      <rect className="f-gR" x="-76" y="340" width="152" height="6" />
    </g>
  )
}

/* Pad shadow on a receiving surface, clipped to that surface. It exists only
   while its caster is near (scene.css steps it off above h 60). */
function PadShadow({ id, surface, tone }) {
  return (
    <g clipPath={`url(#${id}${surface})`}>
      <g className="pp-shadow">
        <circle className={tone} r="60" transform={FACE_UNIT} />
      </g>
    </g>
  )
}

function Dome() {
  return (
    <g className="pp-dome">
      <path className="f-si" d={DOME} />
      <path className="f-siL" d={DOME_LIT} />
      <path className="f-siS" d={DOME_SHADE} />
      <path className="f-siC" d={DOME_CORE} />
      <path className="l-domeSpec" d={DOME_SPEC} />
      <path className="l-rimLit" d={RIM_LIT} />
      <path className="l-rimBack" d={RIM_BACK} />
      <path className="l-lip pp-lip" d={LIP} />
    </g>
  )
}

/* The bulge ring behind the dome: only its sides show, proud of the flanks. */
function BulgeBack({ shape, className }) {
  return (
    <g className={`pp-bulge ${className}`}>
      <path className="f-siS" d={shape.back} />
      <path className="f-siL" d={shape.backLit} />
    </g>
  )
}

/* Its front half is the pad's bottom outline: body, the lit flank, the shade
   side, the core band where the sole turns under, a lit rim while rolling. */
function BulgeFront({ shape, className, seam = false }) {
  return (
    <g className={`pp-bulge ${className}`}>
      <path className="f-si" d={shape.front} />
      <path className="f-siL" d={shape.frontLit} />
      <path className="f-siS" d={shape.frontShade} />
      <path className="f-siC" d={shape.core} />
      {seam && <path className="l-seam" d={shape.seam} />}
      <path className="l-front pp-front" d={shape.rim} />
    </g>
  )
}

/* The silicone dome inside the air clip (the floor is the cliché face or the
   crown arc), between the two halves of its bulge ring: flat on the cliché,
   following the crown on the cap. */
function Pad({ id }) {
  return (
    <>
      <BulgeBack shape={BULGE_FLAT} className="pp-bulge--flat" />
      <BulgeBack shape={BULGE_ARCH} className="pp-bulge--arch" />
      <g clipPath={`url(#${id}air)`}>
        <Dome />
      </g>
      <BulgeFront shape={BULGE_FLAT} className="pp-bulge--flat" />
      <BulgeFront shape={BULGE_ARCH} className="pp-bulge--arch" seam />
    </>
  )
}

/* Detail A: the pad face from below, where the reversed mark blooms with the
   contact patch. Its origin rides the contact plane; the source ring sits on
   the plane just right of the patch and the leader climbs through open
   polymer, clear of the pad and its holder, into the bubble. */
function DetailA({ id }) {
  return (
    <g className="pp-detail-a">
      <g className="pp-detail-src">
        <circle className="l-anno l-dd" cx="78" cy="-2" r="14" />
        <path className="l-leader" d="M87.5 -12.3L144.7 -104.7" />
      </g>
      <g className="pp-bubble">
        <g transform="translate(190 -150)">
          <circle className="f-floor l-bubble" r="64" />
          <circle className="f-si" r="52" />
          <path className="f-siL" d={LIT_CRESCENT} />
          <path className="f-siC" d={CORE_CRESCENT} />
          <circle className="l-contour" r="36" />
          <g clipPath={`url(#${id}bloom)`}>
            <g transform="scale(0.62)">
              <IsofixGlyph className="pp-glyph g-ink" mirrored />
            </g>
          </g>
        </g>
      </g>
    </g>
  )
}

/* The payoff: crops fly in on the cap face, the leader draws into the throat
   from a dot set clear of the top-right crop. */
const REG_LEADER = 'M1188 523L1236 480H1420'

function Register() {
  return (
    <g className="pp-register">
      <CropMarks transform={FACE_AT(1124)} />
      <g className="pp-reg-leader">
        <path className="l-knock" d={REG_LEADER} />
        <path className="l-signal" d={REG_LEADER} />
      </g>
      <circle className="f-live pp-reg-dot" cx="1188" cy="523" r="3.5" />
    </g>
  )
}

/** The machine, back to front. */
function Machine({ id }) {
  return (
    <>
      <Floor />
      <Cabinet />
      <Table />
      <CFrame />
      <AirService />
      <DragChain />
      <XAxis />
      <CupDrive />
      <CupRod />
      <ClicheBase id={id} />
      <PadShadow id={id} surface="clicheFace" tone="f-sE" />
      <ClicheTop />
      <InkCup />
      <Fixture />
      <CapBody id={id} />
      <PadShadow id={id} surface="capFace" tone="f-pR" />
      <CapMark />
      <PadRods />
      <Carriage />
      <PadHolder />
      <Pad id={id} />
      <Register />
      <DetailA id={id} />
    </>
  )
}

export function PadStage({ id }) {
  return (
    <>
      <PadDefs id={id} />
      <g className="pp-cam">
        <Machine id={id} />
      </g>
    </>
  )
}
